import React, { useState, ChangeEvent } from 'react';
import { FaSave } from 'react-icons/fa';
import CurrencyInput from '~/components/Modules/CurrencyMask';
import './styles.css';
import { Checkbox } from '@mui/material';

interface AddPriceModalProps {
    unit_id: number;
    unitContractPriceType: string;
    defaultYear?: number;
    onSave: (priceData: any) => void;
    onCancel: () => void;
}

const AddPriceModal: React.FC<AddPriceModalProps> = ({ unit_id, unitContractPriceType, defaultYear, onSave, onCancel }) => {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth() + 1;
	const pad2 = (n: number) => String(n).padStart(2, '0');
    
    const [formData, setFormData] = useState({
        unit_id: unit_id,
        price: 0,
        type: unitContractPriceType
    });
	const [period, setPeriod] = useState({
		start: `${defaultYear || currentYear}-${pad2(currentMonth)}`,
		end: `${defaultYear || currentYear}-${pad2(currentMonth)}`
	});
	const [useRange, setUseRange] = useState(true);

	// No generic onChange needed for month/year; handled inline for rangeData

    function onChangeFloat(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, originalValue: any) {
        const { name } = event.target;
        setFormData(prev => ({ ...prev, [name]: originalValue }));
    }

    // removed month/year select helpers; we now use input type="month"

	function handleSave() {
		if (!formData.price || formData.price <= 0) {
            alert('Por favor, insira um valor válido para o preço.');
            return;
        }
        
		// Construir lista de (ano, mês) inclusiva a partir de inputs type="month"
		if (!period.start || !period.end) {
			alert('Informe o período (início e fim).');
		 return;
		}
		const [sY, sM] = period.start.split('-').map(Number);
		const [eY, eM] = period.end.split('-').map(Number);
		const start = { y: sY, m: sM };
		const end = { y: eY, m: eM };

		// Validar ordem
		if (start.y > end.y || (start.y === end.y && start.m > end.m)) {
			alert('Período inválido: a data inicial deve ser anterior ou igual à data final.');
			return;
		}

		const items: any[] = [];
		let y = start.y, m = start.m;
		while (y < end.y || (y === end.y && m <= end.m)) {
			items.push({
				unit_id: unit_id,
				year: y,
				month: m,
				price: formData.price,
				type: unitContractPriceType
			});
			m += 1;
			if (m === 13) {
				m = 1;
				y += 1;
			}
		}

		onSave(items);
    }

    function handleCancel() {
        onCancel();
    }

    return (
        <div className="price-modal-container">
            <div className="price-modal-header">
                <h3>Adicionar Novo Preço</h3>
            </div>

            <div className="price-modal-body">
				<div className="form-group">
					<label>Período:</label>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 10px 0' }}>
						<Checkbox
							checked={useRange}
							onChange={(e) => setUseRange(e.target.checked)}
							size="small"
						/>
						<span style={{ fontSize: 12, color: '#6b7280' }}>Intervalo (desmarque para um único mês)</span>
					</div>

					{useRange ? (
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
							<div>
								<label style={{ fontSize: 12, color: '#6b7280' }}>Início</label>
								<input
									type="month"
									className="form-input"
									value={period.start}
									max={period.end}
									onChange={(e) => {
										const next = e.target.value;
										setPeriod(prev => {
											// se início ultrapassar o fim, ajusta o fim para o início
											if (prev.end && next > prev.end) {
												return { start: next, end: next };
											}
											return { ...prev, start: next };
										});
									}}
								/>
							</div>
							<div>
								<label style={{ fontSize: 12, color: '#6b7280' }}>Fim</label>
								<input
									type="month"
									className="form-input"
									value={period.end}
									min={period.start}
									onChange={(e) => {
										const next = e.target.value;
										setPeriod(prev => {
											// se fim for menor que o início, ajusta o início para o fim
											if (prev.start && next < prev.start) {
												return { start: next, end: next };
											}
											return { ...prev, end: next };
										});
									}}
								/>
							</div>
						</div>
					) : (
						<div style={{ maxWidth: 280 }}>
							<label style={{ fontSize: 12, color: '#6b7280' }}>Mês</label>
							<input
								type="month"
								className="form-input"
								value={period.start}
								onChange={(e) => {
									const next = e.target.value;
									setPeriod({ start: next, end: next });
								}}
							/>
						</div>
					)}
				</div>

				<div className="form-group">
                    <label htmlFor="type">Tipo:</label>
                    <input
                        id="type"
                        name="type"
                        value={unitContractPriceType === 'fixed' ? 'Fixo' : 'Desconto garantido'}
                        type="text"
                        className="form-input"
                        disabled
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="price">Valor:</label>
                    <CurrencyInput
                        id="price"
                        name="price"
                        placeholder="0,00"
                        onChangeValue={onChangeFloat}
                        value={formData.price}
                        hideSymbol
                        className="form-input"
                    />
                </div>
            </div>

            <div className="price-modal-footer">
                <button className="btn-cancel" onClick={handleCancel}>
                    Cancelar
                </button>
                <button className="btn-save" onClick={handleSave}>
                    <FaSave size={16} />
                    Adicionar Preço
                </button>
            </div>
        </div>
    );
};

export default AddPriceModal;
